import { useState, useMemo, useEffect } from "react";
import { ArrowRight, Calendar, Clock, MapPin } from "lucide-react";
import styles from "./AlumniEventsTabs.module.css";
import Image from "next/image";

interface Button {
  id: number;
  label: string;
  url: string;
}

interface BulletPoint {
  id: number;
  label: string;
  url: string | null;
}

interface ApiEventItem {
  id: number;
  title: string;
  description: string;
  sub_description?: string;
  date_time: string;
  location: string;
  learn_more_btn?: Button;
  bullet_points?: BulletPoint[];
}

interface AlumniEventsTabsProps {
  data?: {
    heading?: string;
    view_all_btn?: Button;
    feature_event_cta_cards?: ApiEventItem[];
  };
}

// Helper function to format date from ISO string
const formatDate = (isoDate: string): { date: string; time: string } => {
  try {
    const date = new Date(isoDate);
    return {
      date: date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  } catch (error) {
    console.error("Error formatting date:", error);
    return { date: "Invalid date", time: "Invalid time" };
  }
};

export default function AlumniEventsTabs({ data }: AlumniEventsTabsProps) {
  const [selected, setSelected] = useState(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    // Check if we're on the client side
    if (typeof window !== "undefined") {
      // Set initial state
      setIsMobile(window.innerWidth <= 900);

      // Add event listener for window resize
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 900);
      };

      window.addEventListener("resize", handleResize);

      // Clean up event listener
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  // Process events data from API
  const processedEvents = useMemo(() => {
    if (!data?.feature_event_cta_cards?.length) {
      return [];
    }

    return data.feature_event_cta_cards.map((event) => {
      const { date, time } = formatDate(event.date_time);

      return {
        id: event.id,
        title: event.title,
        date,
        time,
        location: event.location,
        description: event.description,
        sub_description: event.sub_description,
        bullet_points: event.bullet_points,
        learn_more_url: event.learn_more_btn?.url,
        learn_more_label: event.learn_more_btn?.label,
        details: (
          <>
            <h3>{event.title}</h3>
            {event.description && <strong>{event.description}</strong>}
            <div className={styles["alumni-meta"]}>
              <span>
                <Calendar size={18} /> {date}
              </span>
              <span>
                <Clock size={18} /> {time}
              </span>
            </div>

            {event.sub_description && <p>{event.sub_description}</p>}

            {event.bullet_points && event.bullet_points.length > 0 && (
              <ul className={styles["alumni-roles"]}>
                {event.bullet_points.map((point) => (
                  <li key={point.id}>
                    <b>{point.label}</b>
                  </li>
                ))}
              </ul>
            )}

            {event.learn_more_btn?.url && (
              <button
                className={styles["learn-more"]}
                onClick={() => window.open(event.learn_more_btn?.url, "_blank")}
              >
                {event.learn_more_btn?.label || "Learn More"}
                <ArrowRight size={22} />
              </button>
            )}
          </>
        ),
      };
    });
  }, [data]);

  if (!processedEvents.length) {
    return null;
  }

  return (
    <div className="container" id="events">
      <div className={styles.alumniEventstab}>
        {/* Events List (Tabs) */}
        <section className={styles["events-list"]} style={{ flex: 1 }}>
          <div className={styles["events-header"]}>
            <h2>{data?.heading}</h2>
            {data?.view_all_btn && (
              <div className={styles.viewAllBtn}>
                <a href={data.view_all_btn.url} className={styles["view-all"]}>
                  {data.view_all_btn.label}
                </a>
                <ArrowRight size={22} />
              </div>
            )}
          </div>

          <div className={styles.events}>
            {processedEvents.map((event, idx) => (
              <div
                key={event.id}
                className={`${styles["event-item"]} ${
                  selected === idx ? styles.highlight : ""
                }`}
                style={{ cursor: "pointer" }}
                onClick={() => setSelected(idx)}
              >
                <div className={styles["event-number"]}>
                  <Image
                    src={`/assets/number${idx + 1}.png`}
                    alt={`Number ${idx + 1}`}
                    width={isMobile ? 40 : 72}
                    height={isMobile ? 40 : 45}
                    style={{
                      objectFit: "contain",
                      width: isMobile ? "40px" : "72px",
                      height: isMobile ? "40px" : "45px",
                    }}
                    className={
                      idx + 1 === 2
                        ? selected === idx
                          ? ""
                          : styles["inverted-image"]
                        : selected === idx
                        ? styles["inverted-image"]
                        : ""
                    }
                  />
                </div>
                <div className={styles["event-details"]}>
                  <h3>{event.title}</h3>
                  <div className={styles["event-meta"]}>
                    <span>
                      <Calendar size={18} /> {event.date}
                    </span>
                    <span>
                      <Clock size={18} /> {event.time}
                    </span>
                    {event.location && (
                      <span>
                        <MapPin size={18} /> {event.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Event Details */}
        <section className={styles["alumniassociation"]}>
          <div className={styles["alumni-content"]}>
            {processedEvents[selected]?.details}
          </div>
        </section>
      </div>
    </div>
  );
}
